import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Error, Info, OneColored, OpenInNew } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  IconSource,
  Loader,
  PromoBannerV2,
  ShadowToken,
  SpacingToken,
  Text,
  Tooltip,
  TransitionDurationToken,
} from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import Flex from "components/layout/Flex";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useAgentFlag } from "hooks/use-agent-flag";
import { actions$ } from "store/action-stream";
import { TICKET, TicketActions } from "store/entities/tickets/actions";
import { IAction } from "store/helper";
import { selectSimilarTickets } from "store/views/tickets/selectors";
import { textEllipsisStyles } from "styles/common";
import { withVar } from "themes/helpers";

import { SectionWrapper } from "./styles";

interface IProps {
  currentTicketId: string;
}

export const TicketDetailsSimilarTickets: FC<IProps> = ({ currentTicketId }) => {
  const dispatch = useDispatch();
  const promoScrollRef = useRef<HTMLDivElement>(null);

  const [closedPromoBannerFlag, setClosedPromoBannerFlag] = useAgentFlag("hasClosedSimilarTicketsBanner");
  const [wasScrolledIntoView, setWasScrolledIntoView] = useAgentFlag("wasSimilarTicketScrolledIntoView");
  // eliminate lag between the flag change and the UI update
  const [closedPromoBanner, setClosedPromoBanner] = useState(closedPromoBannerFlag);
  const [opened, setOpened] = useState(!closedPromoBanner);

  const [displayError, setDisplayError] = useState(false);

  const similarTickets = useSelector(selectSimilarTickets);

  const isLoading = closedPromoBanner && !similarTickets && !displayError;

  const isEmpty = similarTickets && similarTickets.length === 0;

  const handleHeaderClick = (): void => {
    if (!opened && closedPromoBanner) {
      handleFetchSimilarTickets();
    }
    setOpened(prevOpened => !prevOpened);

    trackEngagement(
      !opened
        ? "Ticket: Similar tickets - expand similar tickets"
        : "Ticket: Similar tickets - collapse similar tickets",
      EventPlace.Tickets
    );
  };

  const handleClosePromoBanner = (): void => {
    setClosedPromoBanner(true);
    setClosedPromoBannerFlag(true);
    setOpened(false);
    trackEngagement("Ticket: Similar tickets onboarding banner action", EventPlace.Tickets, { action: "skipped" });
  };

  const handleFetchSimilarTickets = (): void => {
    setDisplayError(false);
    dispatch(TicketActions.fetchSimilarTickets({ ticketId: currentTicketId }));
  };

  const handleClickSimilarTicket = (ticketId: string): void => {
    trackEngagement("Ticket: Similar tickets - clicked", EventPlace.Tickets, { ticketId });
  };

  const handleRetryFetchSimilarTickets = (): void => {
    trackEngagement("Ticket: Similar tickets - retry after finding failed", EventPlace.Tickets);
    handleFetchSimilarTickets();
  };

  const handleShowSimilarTickets = (): void => {
    setClosedPromoBanner(true);
    setClosedPromoBannerFlag(true);
    handleFetchSimilarTickets();
    trackEngagement("Ticket: Similar tickets onboarding banner action", EventPlace.Tickets, { action: "clicked" });
  };

  useEffect(() => {
    const unsubscribe = pipe(
      actions$,
      filter(
        (action: IAction) =>
          action.type === TICKET.FETCH_SIMILAR_TICKETS.SUCCESS || action.type === TICKET.FETCH_SIMILAR_TICKETS.FAILURE
      ),
      subscribe((action: IAction) => {
        if (action.type === TICKET.FETCH_SIMILAR_TICKETS.FAILURE) {
          setDisplayError(true);
        }
      })
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (promoScrollRef.current && !wasScrolledIntoView && !closedPromoBanner) {
      setTimeout(() => {
        setWasScrolledIntoView(true);
        promoScrollRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 1000);
    }
  }, [promoScrollRef.current, wasScrolledIntoView, closedPromoBanner]);

  return (
    <SectionWrapper borderTop>
      <AccordionItem
        title={
          <AccordionHeader>
            <Tooltip triggerRenderer={() => <Icon source={OneColored} />} placement="top" kind="invert">
              Similar tickets found by Text Copilot
            </Tooltip>
            <Heading size="xs">Similar tickets</Heading>
          </AccordionHeader>
        }
        headerTestID="similar-tickets-accordion"
        opened={opened}
        onHeaderClick={handleHeaderClick}
      >
        <AccordionContent>
          {!closedPromoBanner && (
            <>
              <PromoBannerV2
                onClose={handleClosePromoBanner}
                primaryButton={{ label: "Show similar tickets", handleClick: handleShowSimilarTickets, kind: "text" }}
                css={{
                  maxWidth: "900px",
                  background: withVar(DesignToken.SurfaceGradient10),
                  borderImage: withVar(DesignToken.OnePopoverBorderDefault),
                  animation: wasScrolledIntoView ? `1s 1s ${PromoAnimation} ease-in-out` : "none",
                }}
              >
                <Text bold>Find similar tickets to this case</Text>
                <Text>
                  Use Text Copilot to find tickets like the one you&apos;re working on and apply tried-and-true
                  solutions to get things sorted quickly.
                </Text>
              </PromoBannerV2>
            </>
          )}
          {isLoading && (
            <CenterContainer>
              <Loader />
            </CenterContainer>
          )}
          {displayError && (
            <CenterContainer>
              <Overview
                icon={Error}
                kind="error"
                title="Finding similar tickets failed"
                description="Please try again to search for related tickets."
                primaryButton={{ label: "Try again", handleClick: handleRetryFetchSimilarTickets }}
              />
            </CenterContainer>
          )}
          {isEmpty && (
            <CenterContainer>
              <Overview
                icon={Info}
                kind="info"
                title="No similar tickets were found"
                description="There are no matching tickets available right now. Once you resolve a similar case, it'll appear here."
              />
            </CenterContainer>
          )}
          {similarTickets && (
            <SimilarTicketsContainer>
              {similarTickets.map(ticket => (
                <SimilarTicket
                  kind="link-light"
                  key={ticket.id}
                  href={`/tickets/${ticket.id}`}
                  target="_blank"
                  onClick={() => handleClickSimilarTicket(ticket.id)}
                >
                  <div css={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Text css={textEllipsisStyles}>{ticket.subject}</Text>
                    <Icon source={OpenInNew} size="small" css={{ marginLeft: "auto" }} />
                  </div>
                </SimilarTicket>
              ))}
            </SimilarTicketsContainer>
          )}
        </AccordionContent>
      </AccordionItem>
      <div ref={promoScrollRef} />
    </SectionWrapper>
  );
};

const PromoAnimation = keyframes`
  from {
    box-shadow: none;
  }
  50% {
    box-shadow: ${withVar(ShadowToken.Focus)};
  }
  to {
    box-shadow: none;
  }
`;

interface IOverviewProps {
  icon: IconSource;
  title: string;
  kind: "error" | "info";
  description: string;
  primaryButton?: {
    label: string;
    handleClick: () => void;
  };
}

const Overview: FC<IOverviewProps> = ({ icon, title, description, primaryButton, kind }) => {
  return (
    <OverviewContainer>
      <Icon
        source={icon}
        size="xxlarge"
        customColor={withVar(
          kind === "error" ? DesignToken.SurfaceAccentEmphasisHighNegative : DesignToken.SurfaceAccentEmphasisHighInfo
        )}
      />
      <Heading size="sm" textAlign="center">
        {title}
      </Heading>
      <Text textAlign="center" customColor={withVar(DesignToken.ContentBasicSecondary)}>
        {description}
      </Text>
      {primaryButton && (
        <Button kind="high-contrast" size="compact" onClick={primaryButton.handleClick}>
          {primaryButton.label}
        </Button>
      )}
    </OverviewContainer>
  );
};

const OverviewContainer = styled(Flex)`
  flex-direction: column;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
  text-wrap: pretty;
`;

const AccordionHeader = styled(Flex)`
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
`;

const AccordionContent = styled.div`
  max-height: 192px;
`;

const CenterContainer = styled(Flex)`
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const SimilarTicket = styled(Button)`
  text-align: left;
  width: 100%;
  justify-content: space-between;

  span {
    margin-left: ${withVar(SpacingToken.Spacing2)};
    color: ${withVar(DesignToken.ActionNeutralDefault)};
    transition: color ${withVar(TransitionDurationToken.Fast2)} ease-in-out;
  }

  &:hover span {
    color: ${withVar(DesignToken.ActionNeutralHover)};
  }

  & > div {
    width: 100%;
  }
`;

const SimilarTicketsContainer = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: ${withVar(SpacingToken.Spacing2)};
`;
